import ExcelJS from 'exceljs';

const HEADER_FILL = 'FF312E81';
const SECTION_FILL = 'FFE9D8FD';
const BANNER_FILL = 'FFF3F0FF';

const HEADERS = [
  'Test No#',
  'Expected Results',
  'Preconditions',
  'Sequence',
  'Caller Experience',
  'Agent Experience',
  'Pass/Fail',
  'Comments'
];

function mergeAndStyle(ws, range, value, style = {}) {
  ws.mergeCells(range);
  const cell = ws.getCell(range.split(':')[0]);
  cell.value = value;
  if (style.fill) {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: style.fill } };
  }
  if (style.font) cell.font = style.font;
  if (style.alignment) cell.alignment = style.alignment;
  if (style.border) cell.border = style.border;
}

/**
 * Build a blank SIT Excel test-script template (single sheet) and trigger a
 * browser download. Layout mirrors the standard vendor QA template so the
 * deterministic parser in ExcelImporter.parseWorkbook() picks it up without
 * configuration. Includes two example test cases so non-technical authors see
 * the row pattern for "Caller hears", "Press", "Say", and "Pass if Call
 * disconnects" flows.
 */
export async function downloadExcelTemplate(filename = 'sit-test-script-template.xlsx') {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Presidio SIT';
  const ws = wb.addWorksheet('Main Callflow');

  ws.columns = [
    { width: 10 }, // Test No#
    { width: 36 }, // Expected Results
    { width: 28 }, // Preconditions
    { width: 10 }, // Sequence
    { width: 60 }, // Caller Experience
    { width: 28 }, // Agent Experience
    { width: 12 }, // Pass/Fail
    { width: 24 }  // Comments
  ];

  mergeAndStyle(ws, 'A1:H1', 'QA Test Scenarios (Main Call Flow)', {
    fill: HEADER_FILL,
    font: { bold: true, color: { argb: 'FFFFFFFF' }, size: 14 },
    alignment: { horizontal: 'center', vertical: 'middle' }
  });
  ws.getRow(1).height = 24;

  const bannerStyle = {
    fill: BANNER_FILL,
    font: { bold: true },
    alignment: { vertical: 'middle' }
  };
  mergeAndStyle(ws, 'A2:B2', 'Queue Name', bannerStyle);
  mergeAndStyle(ws, 'C2:H2', '', { fill: BANNER_FILL });
  mergeAndStyle(ws, 'A3:B3', 'Production TFN', bannerStyle);
  mergeAndStyle(ws, 'C3:H3', '', { fill: BANNER_FILL });
  mergeAndStyle(ws, 'A4:B4', 'AWS DIDs', bannerStyle);
  mergeAndStyle(ws, 'C4:H4', '', { fill: BANNER_FILL });

  const headerRow = ws.getRow(5);
  HEADERS.forEach((h, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = h;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL } };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
  });
  headerRow.height = 22;

  // Section banner — merged across the row, parser uses this to tag plans.
  mergeAndStyle(ws, 'A6:H6', 'Greeting & Main Menu (example section)', {
    fill: SECTION_FILL,
    font: { bold: true, italic: true },
    alignment: { vertical: 'middle' }
  });

  // Example Test #1 — three steps, demonstrates "Caller hears" + "Press" + hangup.
  const t1Rows = [
    { seq: 1, caller: 'Dial the Main Inbound test TFN' },
    {
      seq: 2,
      caller:
        'Caller hears the Greeting message: "Thank you for calling Acme. Press 1 for sales, 2 for support."'
    },
    { seq: 3, caller: 'Press "1" for Sales' },
    { seq: 4, caller: 'Pass if Caller hears "Connecting you to a Sales representative"' },
    { seq: 5, caller: 'Fail if Not' }
  ];
  fillTest(ws, 7, '1', 'Validate Sales menu path', 'Hours of Operation: Open', t1Rows);

  // Blank separator row between test cases.
  ws.getRow(7 + t1Rows.length).height = 8;

  // Section banner #2 — different scenario.
  const sec2Row = 7 + t1Rows.length + 1;
  mergeAndStyle(ws, `A${sec2Row}:H${sec2Row}`, 'After-Hours / Voicemail (example section)', {
    fill: SECTION_FILL,
    font: { bold: true, italic: true },
    alignment: { vertical: 'middle' }
  });

  // Example Test #2 — demonstrates "Say" + speech + DTMF + hangup.
  const t2Rows = [
    { seq: 1, caller: 'Dial the Main Inbound test TFN' },
    { seq: 2, caller: 'Caller hears the After Hours message: "Our offices are now closed."' },
    { seq: 3, caller: 'Say "Voicemail"' },
    { seq: 4, caller: 'Caller hears the voicemail prompt: "Please leave a message after the tone."' },
    {
      seq: 5,
      caller:
        'After the beep, caller leaves a voicemessage "This is a test call" and presses #'
    },
    { seq: 6, caller: 'Pass if the Call disconnects after pressing #' },
    { seq: 7, caller: 'Fail if Not' }
  ];
  fillTest(
    ws,
    sec2Row + 1,
    '2',
    'After-hours voicemail path',
    'Hours of Operation: Closed\nHoliday: No',
    t2Rows
  );

  // Wrap-text on long-text columns.
  ws.eachRow((row) => {
    row.getCell(2).alignment = { wrapText: true, vertical: 'top' };
    row.getCell(3).alignment = { wrapText: true, vertical: 'top' };
    row.getCell(5).alignment = { wrapText: true, vertical: 'top' };
    row.getCell(6).alignment = { wrapText: true, vertical: 'top' };
  });

  ws.views = [{ state: 'frozen', ySplit: 5 }];

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function fillTest(ws, startRow, testNo, expectedResults, preconditions, rows) {
  const endRow = startRow + rows.length - 1;
  ws.mergeCells(`A${startRow}:A${endRow}`);
  ws.mergeCells(`B${startRow}:B${endRow}`);
  ws.mergeCells(`C${startRow}:C${endRow}`);
  ws.getCell(`A${startRow}`).value = testNo;
  ws.getCell(`A${startRow}`).alignment = { vertical: 'top', horizontal: 'center' };
  ws.getCell(`B${startRow}`).value = expectedResults;
  ws.getCell(`B${startRow}`).alignment = { wrapText: true, vertical: 'top' };
  ws.getCell(`C${startRow}`).value = preconditions;
  ws.getCell(`C${startRow}`).alignment = { wrapText: true, vertical: 'top' };

  rows.forEach((r, i) => {
    const row = ws.getRow(startRow + i);
    row.getCell(4).value = r.seq;
    row.getCell(4).alignment = { vertical: 'top', horizontal: 'center' };
    row.getCell(5).value = r.caller;
    row.getCell(5).alignment = { wrapText: true, vertical: 'top' };
    row.getCell(6).value = r.agent || 'N/A';
    row.getCell(6).alignment = { vertical: 'top' };
  });
}
